import type { Product, Customer, Contact, OrderForm, Order } from './types';
import { API_BASE_URL } from './config';

export async function searchTestingResults(batchNumber: string): Promise<string | null> {
  try {
    const response = await fetch('https://testing.stickysaguaro.com/wp-json/custom-api/v1/list-pdfs');
    if (!response.ok) throw new Error('Failed to fetch PDF list');
    const pdfFiles = await response.json();
    const matchingPdf = pdfFiles.find((file: string) => file.toLowerCase().includes(batchNumber.toLowerCase()));
    if (matchingPdf) {
      const encoded = btoa(unescape(encodeURIComponent(matchingPdf)));
      return `https://testing.stickysaguaro.com/wp-json/custom-api/v1/get-pdf/${encoded}`;
    }
    return null;
  } catch (error) {
    console.error('Error searching testing results:', error);
    return null;
  }
}

export async function fetchDashboardStats(): Promise<{
  totalProducts: number;
  lowStockProducts: number;
  totalQoh: number;
  activeOrderLinks: number;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/getDashboardStats.php`);
    if (!response.ok) {
      throw new Error('Failed to fetch dashboard stats');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
}

export async function fetchProducts(): Promise<Product[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/getProducts.php`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to fetch products');
    }
    const data = await response.json();
    return data.map((product: any) => ({
      ...product,
      unitSize: product.unitSize || '', // Ensure empty string if unitSize is null/undefined
      qoh: Number(product.qoh) // Ensure QOH is a number
    }));
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

export async function createProduct(product: Omit<Product, 'id'>): Promise<Product> {
  try {
    const response = await fetch(`${API_BASE_URL}/createProduct.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        ...product,
        unitSize: product.unitSize || '', // Ensure empty string if unitSize is null/undefined
        qoh: Number(product.qoh),
        price: String(product.price),
        totalCbd: String(product.totalCbd),
        totalThc: String(product.totalThc),
        totalCannabinoids: String(product.totalCannabinoids),
        isExclusive: Boolean(product.isExclusive),
        archived: Boolean(product.archived),
        images: Array.isArray(product.images) ? product.images : [],
        exclusiveCustomers: Array.isArray(product.exclusiveCustomers) ? product.exclusiveCustomers : []
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to create product');
    }

    const data = await response.json();
    return {
      ...data,
      unitSize: data.unitSize || '', // Ensure empty string if unitSize is null/undefined
      qoh: Number(data.qoh)
    };
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

export async function updateProduct(product: Product): Promise<Product> {
  try {
    const response = await fetch(`${API_BASE_URL}/updateProduct.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        ...product,
        unitSize: product.unitSize || '', // Ensure empty string if unitSize is null/undefined
        qoh: Number(product.qoh),
        price: String(product.price),
        totalCbd: String(product.totalCbd),
        totalThc: String(product.totalThc),
        totalCannabinoids: String(product.totalCannabinoids),
        isExclusive: Boolean(product.isExclusive),
        archived: Boolean(product.archived),
        images: Array.isArray(product.images) ? product.images : [],
        exclusiveCustomers: Array.isArray(product.exclusiveCustomers) ? product.exclusiveCustomers : []
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to update product');
    }

    const data = await response.json();
    return {
      ...data,
      unitSize: data.unitSize || '', // Ensure empty string if unitSize is null/undefined
      qoh: Number(data.qoh)
    };
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
}

export async function deleteProduct(productId: string): Promise<{
  success: boolean;
  action: 'deleted' | 'inactivated';
  message: string;
  orderCount?: number;
}> {
  try {
    // Use POST instead of DELETE for better server compatibility
    const response = await fetch(`${API_BASE_URL}/deleteProduct.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: productId })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to delete product');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
}

export async function fetchOrders(): Promise<Order[]> {
  const response = await fetch(`${API_BASE_URL}/getOrders.php`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    throw new Error(errorData.error || 'Failed to fetch orders');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function updateOrderStatus(orderId: string, status: Order['status']): Promise<Order> {
  const response = await fetch(`${API_BASE_URL}/updateOrderStatus.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderId, status }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    throw new Error(errorData.error || 'Failed to update order status');
  }

  return await response.json();
}

export async function updateOrder(orderData: Partial<Order> & { id: string }): Promise<Order> {
  const response = await fetch(`${API_BASE_URL}/updateOrder.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    throw new Error(errorData.error || 'Failed to update order');
  }

  return await response.json();
}

export async function fetchFulfillmentOrders(): Promise<Order[]> {
  const response = await fetch(`${API_BASE_URL}/getFulfillmentOrders.php`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    throw new Error(errorData.error || 'Failed to fetch fulfillment orders');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function updateFulfillment(orderId: string, items: Array<{
  id: string;
  quantityFulfilled: number;
}>): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/updateFulfillment.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderId, items }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    throw new Error(errorData.error || 'Failed to update fulfillment');
  }
}

export async function fetchCustomers(): Promise<Customer[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/getCustomers.php`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to fetch customers');
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
}

export async function fetchActiveCustomers(): Promise<{ id: string; companyName: string; }[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/getActiveCustomers.php`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to fetch active customers');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching active customers:', error);
    throw error;
  }
}

export async function createCustomer(customer: Omit<Customer, 'id' | 'createdAt'>): Promise<Customer> {
  const response = await fetch(`${API_BASE_URL}/createCustomer.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(customer),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to create customer');
  }

  return await response.json();
}

export async function updateCustomer(customer: Customer): Promise<Customer> {
  try {
    const response = await fetch(`${API_BASE_URL}/updateCustomer.php`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        ...customer,
        _method: 'PUT',
        contacts: customer.contacts?.map(contact => ({
          ...contact,
          phone: String(contact.phone),
          mobile: contact.mobile ? String(contact.mobile) : null
        })),
        pricingTiers: customer.pricingTiers?.map(tier => ({
          ...tier,
          minQuantity: Number(tier.minQuantity),
          discountPercentage: Number(tier.discountPercentage)
        })),
        weeklyOrder: customer.weeklyOrder ? {
          ...customer.weeklyOrder,
          dayOfWeek: Number(customer.weeklyOrder.dayOfWeek)
        } : null
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to update customer');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating customer:', error);
    throw error;
  }
}

export async function createOrderForm(data: {
  customerId: string;
  contactId: string;
  email: string;
}): Promise<OrderForm> {
  try {
    const response = await fetch(`${API_BASE_URL}/createOrderForm.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to create order form');
    }

    const createdOrderForm = await response.json();

    // Send email notification
    try {
      const emailResponse = await fetch(`${API_BASE_URL}/sendOrderFormEmail.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          orderId: createdOrderForm.id,
          email: data.email,
          customerName: createdOrderForm.customerName,
          contactName: createdOrderForm.contactName
        }),
      });

      if (!emailResponse.ok) {
        const errorData = await emailResponse.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Email sending failed:', errorData);
        throw new Error(errorData.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send order form email:', error);
      // Don't fail the order form creation, just log the email error
    }

    return createdOrderForm;
  } catch (error) {
    console.error('Error creating order form:', error);
    throw error;
  }
}

export async function fetchOrderForm(id: string): Promise<{
  orderForm: OrderForm;
  products: Product[];
}> {
  const response = await fetch(`${API_BASE_URL}/getOrderForm.php?id=${encodeURIComponent(id)}`);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    throw new Error(errorData.error || 'Failed to fetch order form');
  }

  const data = await response.json();
  return {
    orderForm: {
      ...data.orderForm,
      volume_discount_enabled: Boolean(data.orderForm.volume_discount_enabled),
      pricingTiers: Array.isArray(data.orderForm.pricingTiers) ? data.orderForm.pricingTiers : []
    },
    products: data.products.map((product: any) => ({
      ...product,
      unitSize: product.unitSize || '',
      qoh: Number(product.qoh)
    }))
  };
}

export async function fetchOrderForms(): Promise<OrderForm[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/getOrderForms.php`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to fetch order forms');
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching order forms:', error);
    throw error;
  }
}

export async function createOrder(orderData: {
  orderFormId?: string;
  customerId: string;
  items: Array<{
    productId: string;
    quantity: number;
    price: number;
    discountPercentage: number;
  }>;
}): Promise<Order> {
  try {
    // Validate required fields
    if (!orderData.customerId || !orderData.items || orderData.items.length === 0) {
      throw new Error('Missing required order data');
    }

    // Format and validate items
    const formattedItems = orderData.items.map(item => ({
      productId: item.productId,
      quantity: Math.max(0, Math.floor(item.quantity)),
      price: Number(item.price.toFixed(2)),
      discountPercentage: Math.max(0, Math.min(100, item.discountPercentage))
    }));

    // Validate items
    formattedItems.forEach(item => {
      if (!item.productId || item.quantity <= 0 || isNaN(item.price) || item.price <= 0) {
        throw new Error('Invalid item data');
      }
    });

    const response = await fetch(`${API_BASE_URL}/createOrder.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        orderFormId: orderData.orderFormId,
        customerId: orderData.customerId,
        items: formattedItems
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create order');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
}

export async function fetchDeliveryOrders(): Promise<{
  pendingDeliveries: Order[];
  completedToday: number;
  scheduledTomorrow: number;
}> {
  const response = await fetch(`${API_BASE_URL}/getDeliveryOrders.php`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
    throw new Error(errorData.error || 'Failed to fetch delivery orders');
  }
  return await response.json();
}

export async function fetchCustomerOrders(customerId: string): Promise<Order[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/getCustomerOrders.php?customerId=${encodeURIComponent(customerId)}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
      throw new Error(errorData.error || 'Failed to fetch customer orders');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching customer orders:', error);
    throw error;
  }
}